const API_URL =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.PROD
    ? "https://vektor-markets-api.vektor-api.workers.dev/api"
    : "/api");
export const SUPPORTED_SYMBOLS = [
  "XAUUSD",
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "AUDUSD",
  "USDCAD",
  "USDCHF",
  "NZDUSD",
] as const;
const token = () => sessionStorage.getItem("vektor-session-token");
const timedFetch = (
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 30_000,
) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(input, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timeout),
  );
};
let authentication: Promise<unknown> | null = null;
const language = () =>
  localStorage.getItem("vektor-language") === "ru" ? "ru" : "uz";
const localizedMessage = (code?: string, _fallback?: string) => {
  const messages: Record<string, [string, string]> = {
    TELEGRAM_REQUIRED: [
      "Ilovani Telegram bot ichidan oching.",
      "Откройте приложение через Telegram-бота.",
    ],
    TELEGRAM_DATA_EXPIRED: [
      "Telegram sessiyasi eskirgan. Ilovani yopib, bot orqali qayta oching.",
      "Сессия Telegram устарела. Закройте приложение и откройте его снова через бота.",
    ],
    TELEGRAM_SIGNATURE_INVALID: [
      "Telegram imzosi tasdiqlanmadi. Ilovani bot orqali qayta oching.",
      "Подпись Telegram не подтверждена. Откройте приложение снова через бота.",
    ],
    TELEGRAM_AUTH_FAILED: [
      "Telegram autentifikatsiyasi amalga oshmadi. Ilovani qayta oching.",
      "Не удалось выполнить авторизацию Telegram. Откройте приложение снова.",
    ],
    ANALYSIS_LIMIT_REACHED: [
      "Tahlil limitingiz tugadi. Tarifni yangilang.",
      "Лимит анализов исчерпан. Выберите тариф.",
    ],
    ELITE_REQUIRED: [
      "AI yordamchi faqat ELITE tarifida mavjud.",
      "AI-помощник доступен только на тарифе ELITE.",
    ],
    MARKET_CLOSED: ["Forex bozori hozir yopiq.", "Рынок Forex сейчас закрыт."],
    MARKET_DATA_STALE: [
      "Bozor ma’lumotlari yangilanmagan. Keyinroq qayta urinib ko‘ring.",
      "Рыночные данные не обновлены. Повторите попытку позже.",
    ],
    SPOT_XAU_PROVIDER_REQUIRED: [
      "XAUUSD uchun ishonchli spot ma’lumot provayderi hali ulanmagan.",
      "Для XAUUSD пока не подключён надёжный поставщик спотовых данных.",
    ],
    MARKET_PROVIDER_CIRCUIT_OPEN: [
      "Bozor provayderi vaqtincha himoya rejimida. Bir necha daqiqadan keyin qayta urinib ko‘ring.",
      "Поставщик рыночных данных временно в защитном режиме. Повторите попытку через несколько минут.",
    ],
    MARKET_CHART_UNAVAILABLE: [
      "Jonli grafik ma’lumotini olib bo‘lmadi. Qayta urinib ko‘ring.",
      "Не удалось загрузить данные графика. Попробуйте снова.",
    ],
    INVALID_MARKET_CHART_REQUEST: [
      "Grafik aktivi yoki vaqt oralig‘i noto‘g‘ri.",
      "Некорректный актив или интервал графика.",
    ],
    RATE_LIMITED: [
      "So‘rovlar juda ko‘p. Bir ozdan keyin qayta urinib ko‘ring.",
      "Слишком много запросов. Попробуйте немного позже.",
    ],
    SIGNAL_LOCKED: [
      "Bu signal PRO yoki ELITE tarifida ochiladi.",
      "Этот сигнал доступен на тарифе PRO или ELITE.",
    ],
    INVALID_RECEIPT: [
      "Faqat JPG, PNG yoki PDF chekni 10 MB gacha yuklang.",
      "Загрузите чек JPG, PNG или PDF размером до 10 МБ.",
    ],
    INVALID_PAYMENT: [
      "To‘lov ma’lumotlarini tekshiring.",
      "Проверьте платёжные данные.",
    ],
    PAYMENT_AMOUNT_MISMATCH: [
      "To‘lov summasi tanlangan tarif narxiga mos emas.",
      "Сумма платежа не соответствует выбранному тарифу.",
    ],
    INVALID_NOTIFICATION_PREFERENCES: [
      "Bildirishnoma sozlamalarini tekshiring.",
      "Проверьте настройки уведомлений.",
    ],
  };
  const item = code ? messages[code] : undefined;
  return (
    item?.[language() === "ru" ? 1 : 0] ??
    (language() === "ru"
      ? "Не удалось выполнить запрос."
      : "So‘rov bajarilmadi.")
  );
};
const networkError = () =>
  Object.assign(
    new Error(
      localStorage.getItem("vektor-language") === "ru"
        ? "Нет соединения с сервером. Проверьте интернет и попробуйте снова."
        : "Server bilan aloqa yo‘q. Internetni tekshirib, qayta urinib ko‘ring.",
    ),
    { code: "NETWORK_ERROR" },
  );

export function authenticateTelegram(force = false) {
  if (!force && token()) return Promise.resolve({ authenticated: true });
  if (!force && authentication) return authentication;
  const initData = window.Telegram?.WebApp.initData;
  if (!initData) {
    if (import.meta.env.DEV) return Promise.resolve({ demo: true });
    return Promise.reject(
      Object.assign(new Error(localizedMessage("TELEGRAM_REQUIRED")), {
        code: "TELEGRAM_REQUIRED",
      }),
    );
  }
  authentication = timedFetch(`${API_URL}/auth/telegram`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData }),
  })
    .then(async (response) => {
      const body = (await response.json().catch(() => null)) as {
        data?: { sessionToken: string };
        error?: { message?: string; code?: string };
      } | null;
      if (!response.ok) {
        const technical = body?.error?.message;
        const message = localizedMessage(
          body?.error?.code,
          language() === "ru"
            ? "Не удалось выполнить авторизацию Telegram."
            : "Telegram autentifikatsiyasi amalga oshmadi.",
        );
        throw Object.assign(new Error(message), {
          code: body?.error?.code,
          technical,
        });
      }
      if (!body?.data)
        throw new Error(
          language() === "ru"
            ? "Некорректный ответ авторизации Telegram."
            : "Telegram autentifikatsiyasi javobi noto‘g‘ri.",
        );
      sessionStorage.setItem("vektor-session-token", body.data.sessionToken);
      return body;
    })
    .catch((error) => {
      if (error instanceof TypeError) throw networkError();
      throw error;
    })
    .finally(() => {
      authentication = null;
    });
  return authentication;
}

async function apiFetch(path: string, init: RequestInit = {}, retry = true) {
  await authenticateTelegram();
  const headers = new Headers(init.headers);
  if (token()) headers.set("Authorization", `Bearer ${token()}`);
  let response: Response;
  try {
    response = await timedFetch(`${API_URL}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch {
    throw networkError();
  }
  if (response.status === 401 && window.Telegram?.WebApp.initData && retry) {
    sessionStorage.removeItem("vektor-session-token");
    await authenticateTelegram(true);
    return apiFetch(path, init, false);
  }
  return response;
}
export async function logout() {
  try {
    await apiFetch("/auth/logout", { method: "POST" }, false);
  } finally {
    sessionStorage.removeItem("vektor-session-token");
  }
}
async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, init);
  const body = (await response.json().catch(() => null)) as {
    data?: T;
    error?: { message?: string; code?: string };
  } | null;
  if (!response.ok)
    throw Object.assign(
      new Error(localizedMessage(body?.error?.code, body?.error?.message)),
      { code: body?.error?.code },
    );
  return body!.data as T;
}

export type Access = {
  productCode: "FREE" | "TRIAL" | "PRO" | "ELITE";
  analysisLimit: number | null;
  analysesUsed: number;
  analysesRemaining: number | null;
  bonusAnalyses: number;
  expiresAt: string | null;
  aiAssistant: boolean;
};
export type Catalog = {
  assets: Array<{
    id: string;
    symbol: string;
    type: string;
    precision: number;
  }>;
  strategies: Array<{
    id: string;
    code: string;
    nameUz: string;
    nameRu: string;
  }>;
  timeframes: string[];
};
export const getCatalog = () => json<Catalog>("/catalog");
export type MarketCandle = {
  datetime: string;
  open: number;
  high: number;
  low: number;
  close: number;
};
export type MarketChart = {
  candles: MarketCandle[];
  provider: "twelve_data" | "yahoo_finance";
  providerTimestamp: string | null;
};
export const getMarketCandles = (symbol: string, timeframe: string) =>
  json<MarketChart>(
    `/market/candles?symbol=${encodeURIComponent(symbol)}&timeframe=${encodeURIComponent(timeframe)}`,
  );
export const getAccess = () => json<Access>("/me/access");
export const getStrategies = () =>
  json<
    Array<{
      id: string;
      code: string;
      nameUz: string;
      nameRu: string;
      enabled: number;
    }>
  >("/me/strategies");
export const saveStrategies = (strategyCodes: string[]) =>
  json<{ strategyCodes: string[] }>("/me/strategies", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ strategyCodes }),
  });
export const runAnalysis = (payload: {
  symbol: string;
  timeframe: string;
  strategyCodes: string[];
}) =>
  json<{ id: string; result: AnalysisResult; access: Access }>("/analysis", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
export const getAnalysisHistory = () =>
  json<
    Array<{
      id: string;
      symbol: string;
      timeframe: string;
      status: string;
      resultJson: string | null;
      providerTimestamp: string | null;
      createdAt: string;
    }>
  >("/analysis/history");
export type UserNotification = {
  id: string;
  kind: "signal" | "payment" | "subscription" | "system";
  titleUz: string;
  titleRu: string;
  bodyUz: string;
  bodyRu: string;
  actionUrl: string | null;
  readAt: string | null;
  createdAt: string;
};
export const getNotifications = () =>
  json<{ items: UserNotification[]; unread: number }>("/me/notifications");
export const markNotificationsRead = (ids?: string[]) =>
  json<{ readAt: string }>("/me/notifications/read", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(ids ? { ids } : { all: true }),
  });
export type NotificationPreferences = {
  enabled: boolean | number;
  assetsJson?: string;
  assets?: string[];
  minConfidence: number;
  lifecycleEnabled: boolean | number;
  paymentEnabled: boolean | number;
  expiryEnabled: boolean | number;
  quietStart: string | null;
  quietEnd: string | null;
};
export const getNotificationPreferences = () =>
  json<NotificationPreferences>("/me/notification-preferences");
export const saveNotificationPreferences = (input: {
  enabled: boolean;
  assets: string[];
  minConfidence: number;
  lifecycleEnabled: boolean;
  paymentEnabled: boolean;
  expiryEnabled: boolean;
  quietStart: string | null;
  quietEnd: string | null;
}) =>
  json<typeof input>("/me/notification-preferences", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
export type UserPayment = {
  id: string;
  planCode: string;
  payerName: string;
  amount: number;
  currency: string;
  status: "pending" | "approved" | "rejected";
  adminNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
};
export const getMyPayments = () => json<UserPayment[]>("/me/payments");
export type SignalSummary = {
  id: string;
  symbol: string;
  direction: "BUY" | "SELL";
  status: "published" | "active" | "closed" | "expired" | "cancelled";
  timeframe: string;
  entryMin: number;
  entryMax: number;
  stopLoss: number;
  confidenceScore: number;
  marketRegime: string;
  strategy: string;
  strategyMatches: string | null;
  session: string;
  reasonUz: string;
  reasonRu: string;
  createdAt: string;
  publishedAt: string | null;
  expiresAt: string | null;
  realizedR: number | null;
  resultType: "win" | "loss" | "breakeven" | null;
};
export type SignalDetail = SignalSummary & {
  targets: Array<{
    id: string;
    order: number;
    price: number;
    allocation: number;
    hitAt: string | null;
  }>;
  events: Array<{
    id: string;
    eventType: string;
    targetOrder: number | null;
    price: number | null;
    createdAt: string;
  }>;
};
export const getSignals = () => json<SignalSummary[]>("/signals");
export const getSignal = (id: string) =>
  json<SignalDetail>(`/signals/${encodeURIComponent(id)}`);
export type Analytics = {
  period: string;
  total: number;
  totalR: number;
  wins: number;
  losses: number;
  breakevens: number;
  winRate: number;
  profitFactor: number | null;
  expectancy: number;
  maxDrawdown: number;
  equity: Array<{ day: string; r: number }>;
  dailySignals: Array<{
    day: string;
    total: number;
    tp: number;
    sl: number;
    breakeven: number;
    pending: number;
    expired: number;
  }>;
  assets: Array<{
    symbol: string;
    total: number;
    totalR: number;
    winRate: number;
  }>;
};
export const getAnalytics = (period: "7" | "30" | "90" | "all") =>
  json<Analytics>(`/analytics?period=${period}`);
export type EconomicEvent = {
  id: string;
  title: string;
  currency: "USD" | "EUR" | "GBP" | "JPY" | "AUD" | "CAD" | "CHF" | "NZD";
  impact: "Low" | "Medium" | "High" | "Holiday" | "Unknown";
  scheduledAt: string;
  forecast: string | null;
  previous: string | null;
  actual: string | null;
};
export const getEconomicCalendar = () =>
  json<{
    events: EconomicEvent[];
    source: string;
    lastSync: string | null;
    stale: boolean;
    syncError: string | null;
  }>("/calendar");
export type AiContext = {
  marketClosed: boolean;
  analysisFresh: boolean;
  latestAnalysisAt: string | null;
  liveSignalCount: number;
};
export const getAiHistory = () =>
  json<
    Array<{ role: "user" | "assistant"; content: string; createdAt: string }>
  >("/ai/history");
export const askAi = (message: string, language: "uz" | "ru") =>
  json<{ answer: string; context: AiContext }>("/ai/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, language }),
  });
export type AnalysisResult = {
  status: "signal" | "no_setup";
  symbol: string;
  timeframe: string;
  price: number;
  providerTimestamp: string;
  marketRegime: string;
  confidence: number;
  matches: Array<{
    code: string;
    direction: "BUY" | "SELL";
    score: number;
    reasonUz: string;
    reasonRu: string;
  }>;
  strategyExplanations: Array<{
    code: string;
    matched: boolean;
    direction: "BUY" | "SELL" | null;
    score: number;
    reasonUz: string;
    reasonRu: string;
    checks: Array<{
      code: "strategy" | "market" | "confirmation";
      passed: boolean;
      labelUz: string;
      labelRu: string;
    }>;
  }>;
  indicators: { ema20: number; ema50: number; rsi14: number; atr14: number };
  marketConditions: {
    session:
      | "asia"
      | "london"
      | "overlap"
      | "new_york"
      | "late"
      | "rollover"
      | "unknown";
    volatility: "low" | "normal" | "high" | "extreme";
    volatilityRatio: number;
    tradable: boolean;
    blockers: string[];
  };
  confirmation?: {
    timeframe: string;
    direction: "BUY" | "SELL" | "RANGE";
    aligned: boolean;
  };
  signal?: {
    id?: string;
    direction: "BUY" | "SELL";
    entryMin: number;
    entryMax: number;
    stopLoss: number;
    targets: number[];
    riskReward: number;
    reasonUz: string;
    reasonRu: string;
  };
};

export type MarketStatus = {
  marketClosed: boolean;
  configuredProvider: "twelve_data" | "yahoo_finance";
  summary: { healthy: number; degraded: number; down: number };
  rows: Array<{
    provider: string;
    symbol: string;
    timeframe: string;
    status: "healthy" | "degraded" | "down";
    lastCandleAt: string | null;
    latencyMs: number | null;
    missingCandles: number;
    consecutiveFailures: number;
    circuitOpenUntil: string | null;
    lastError: string | null;
    updatedAt: string;
  }>;
};
export const getMarketStatus = () => json<MarketStatus>("/market/status");

export type ScannerItem = {
  symbol: string;
  timeframe: string;
  status: "signal" | "watch" | "no_setup" | "blocked" | "error";
  direction: "BUY" | "SELL" | null;
  confidence: number;
  setupStrength: number;
  marketRegime: string | null;
  matchesJson: string | null;
  explanationsJson: string | null;
  provider: string | null;
  providerTimestamp: string | null;
  errorCode: string | null;
  scannedAt: string;
};
export const getScanner = () =>
  json<{ marketClosed: boolean; items: ScannerItem[] }>("/scanner");

export type WatchlistItem = {
  id?: string;
  symbol: string;
  timeframe: string;
  strategiesJson?: string;
  strategyCodes?: string[];
  createdAt?: string;
  updatedAt?: string;
};
export const getWatchlist = () => json<WatchlistItem[]>("/me/watchlist");
export const saveWatchlist = (
  items: Array<
    Pick<WatchlistItem, "symbol" | "timeframe"> & { strategyCodes: string[] }
  >,
) =>
  json<{ items: WatchlistItem[] }>("/me/watchlist", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });

export type ReferralOverview = {
  code: string;
  link: string;
  rewardPerQualified: number;
  bonusAnalyses: number;
  referrals: Array<{
    id: string;
    status: "pending" | "qualified" | "rejected";
    rewardAnalyses: number;
    firstName: string | null;
    username: string | null;
    createdAt: string;
    qualifiedAt: string | null;
  }>;
};
export const getReferral = () => json<ReferralOverview>("/me/referral");

export type SupportTicket = {
  id: string;
  category: "payment" | "analysis" | "subscription" | "technical" | "other";
  status: "open" | "waiting_user" | "in_progress" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  subject: string;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
};
export type SupportTicketDetail = SupportTicket & {
  messages: Array<{
    id: string;
    senderType: "user" | "admin";
    body: string;
    createdAt: string;
  }>;
};
export const getSupportTickets = () =>
  json<SupportTicket[]>("/me/support/tickets");
export const createSupportTicket = (input: {
  category: SupportTicket["category"];
  subject: string;
  message: string;
}) =>
  json<{ id: string; status: "open" }>("/me/support/tickets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
export const getSupportTicket = (id: string) =>
  json<SupportTicketDetail>(`/me/support/tickets/${encodeURIComponent(id)}`);
export const replySupportTicket = (id: string, message: string) =>
  json<{ sent: true; createdAt: string }>(
    `/me/support/tickets/${encodeURIComponent(id)}/messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
    },
  );

export async function getPaymentSettings() {
  return json<{
    cardNumber: string;
    cardHolder: string;
    trial7Price: number;
    pro30Price: number;
    elite30Price: number;
    currency: string;
  }>("/payment-settings");
}
export async function submitPayment(formData: FormData) {
  const response = await apiFetch("/payments", {
    method: "POST",
    body: formData,
  });
  const body = (await response.json().catch(() => null)) as {
    data?: unknown;
    error?: { message?: string; code?: string };
  } | null;
  if (!response.ok)
    throw Object.assign(new Error(localizedMessage(body?.error?.code)), {
      code: body?.error?.code,
    });
  return body;
}
